import { Variants } from 'motion/react';

import { LOYALTY_EASING } from 'constants/loyalty.constants';

const ELEMENT_HEIGHT_DESKTOP = 640;

const sharedPadding = {
  paddingLeft: 8,
  paddingRight: 8,
};

const sharedVariants: Variants = {
  hidden: {
    transform: 'translateY(-100%)',
    transition: { ease: LOYALTY_EASING, duration: 0.7 },
    ...sharedPadding,
  },
};

export const saudiRewardsVariants: Variants = {
  initial: {
    transform: 'translateY(calc(106px - 100%))',
    transition: { ease: LOYALTY_EASING, duration: 0.7 },
    ...sharedPadding,
  },
  stacked: {
    transform: 'translateY(calc(132px - 100%)) scaleX(0.95)',
    transition: { ease: LOYALTY_EASING, duration: 0.7 },
    ...sharedPadding,
  },
  completed: {
    transform: 'translateY(-100%)',
    transition: { ease: LOYALTY_EASING, duration: 0.75, delay: 0.7 },
    ...sharedPadding,
  },
  completedAfterUpgradeClose: {
    transform: 'translateY(-100%)',
    transition: { ease: LOYALTY_EASING, duration: 0.75, delay: 0.15 },
    ...sharedPadding,
  },
  ...sharedVariants,
};

export const loyaltyVariants: Variants = {
  initial: {
    transform: 'translateY(calc(124px - 100%))',
    transition: { ease: LOYALTY_EASING, duration: 0.7 },
    ...sharedPadding,
  },
  initialWithoutProgress: {
    transform: 'translateY(calc(96px - 100%))',
    transition: { ease: LOYALTY_EASING, duration: 0.7 },
    ...sharedPadding,
  },
  expanded: {
    transform: `translateY(calc(50vh - ${ELEMENT_HEIGHT_DESKTOP / 2}px))`,
    transition: { ease: LOYALTY_EASING, duration: 0.7 },
    ...sharedPadding,
  },
  expandedMobile: {
    transform: 'translateY(8px)',
    transition: { ease: LOYALTY_EASING, duration: 0.7 },
    ...sharedPadding,
  },
  completed: {
    transform: 'translateY(-100%)',
    transition: { ease: LOYALTY_EASING, duration: 0.7, delay: 0.7 },
    ...sharedPadding,
  },
  completedAfterUpgradeClose: {
    transform: 'translateY(-100%)',
    transition: { ease: LOYALTY_EASING, duration: 0.7, delay: 0.15 },
    ...sharedPadding,
  },
  ...sharedVariants,
};

export const calculateProgressBarWidth = (
  currentPoints: number,
  nextPoints: number,
  nextTierMinPoints: number | undefined,
): { initialWidth: number; finalWidth: number } => {
  if (!nextPoints) {
    return { initialWidth: 100, finalWidth: 100 };
  }

  // Last tier does not have next tier min points, so we add a 1 so we can act on it.
  const safeNextTierMinPoints = nextTierMinPoints ?? nextPoints + 1;

  const initialWidth = Math.max(
    0,
    Math.min(100, (currentPoints / safeNextTierMinPoints) * 100),
  );
  const finalWidth = Math.max(
    0,
    Math.min(100, (nextPoints / safeNextTierMinPoints) * 100),
  );
  return { initialWidth, finalWidth };
};

export const calculateProgressPercentage = (
  userPoints: number,
  currentTierMinPoints: number,
  nextTierMinPoints?: number,
): number => {
  // If there's no next tier (user is at highest tier), return 100%
  if (nextTierMinPoints === undefined) {
    return 100;
  }

  // Calculate the total points needed to move from current tier to next tier
  const pointsRange = nextTierMinPoints - currentTierMinPoints;

  // Calculate how many points the user has earned within this range
  const pointsEarned = userPoints - currentTierMinPoints;

  // Calculate the percentage (clamped between 0 and 100)
  const percentage = Math.min(
    Math.max((pointsEarned / pointsRange) * 100, 0),
    100,
  );

  // Return the percentage, rounded to avoid decimal precision issues
  return Math.round(percentage * 10) / 10; // Round to one decimal place
};
